import type * as React from "react";

import { cn } from "@/lib/utils";

import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  type DateAfter,
  type DateBefore,
  DayPicker,
  type SelectSingleEventHandler,
} from "react-day-picker";

import { buttonVariants } from "@/components/ui/Button";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  disablePast?: boolean;
  disableFuture?: boolean;
  selected?: Date;
  mode: "single";
  onSelect?: SelectSingleEventHandler;
};

const configureDisabledDates = (disablePast = false, disableFuture = false) => {
  const afterMatcher: DateAfter = { after: new Date() };
  const beforeMatcher: DateBefore = { before: new Date() };

  const disabledMatchers: (DateAfter | DateBefore)[] = [];
  if (disableFuture) disabledMatchers.push(afterMatcher);
  if (disablePast) disabledMatchers.push(beforeMatcher);

  return disabledMatchers.length > 0 ? disabledMatchers : undefined;
};

const IconComponents = {
  IconLeft: ({ ...props }) => <ChevronLeft {...props} className="h-4 w-4" />,
  IconRight: ({ ...props }) => <ChevronRight {...props} className="h-4 w-4" />,
};
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  disablePast = false,
  disableFuture = false,
  mode,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      mode="single"
      onSelect={props.onSelect}
      defaultMonth={props.selected || new Date()}
      disabled={configureDisabledDates(disablePast, disableFuture)}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex pt-1 relative justify-between items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "icon", size: "icon" }),
          "h-7 w-7 p-0",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-tertiary rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "custom-cell-class h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-slate-100/50 [&:has([aria-selected])]:bg-button-primary/70 [&:has([aria-selected])]:rounded-lg first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "icon", size: "icon" }),
          "h-9 w-9 p-0 font-normal hover:bg-button-primary/60 hover:text-white disabled:hover:text-tertiary",
        ),
        day_range_end: "day-range-end",
        day_selected:
          "custom-day-selected-class bg-button-primary text-white rounded-lg hover:text-slate-50 focus:bg-button-primary/90 focus:text-white",
        day_today: "bg-slate-100 text-slate-900 rounded-lg",
        day_outside:
          "day-outside text-tertiary opacity-50 aria-selected:bg-slate-100/50 aria-selected:text-tertiary aria-selected:opacity-30",
        day_disabled:
          "text-tertiary opacity-50 hover:cursor-not-allowed hover:bg-transparent hover:opacity-50",
        day_range_middle:
          "aria-selected:bg-slate-100 aria-selected:text-slate-900 ",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={IconComponents}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
